import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const eur = (v, locale) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(v || 0);

const TONE = { green: "bg-emerald-400", orange: "bg-orange-400", red: "bg-red-500" };
const TEXT = { green: "text-emerald-300", orange: "text-orange-300", red: "text-red-300" };

export const useProfit = () => {
  const [p, setP] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/profit").then(({ data }) => alive && setP(data)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return [p, setP];
};

const pctText = (c) => (c.pct === null || c.pct === undefined ? "—" : `${c.pct} %`);

// Margin colour next to a client (green ≥ 30 %, orange 0–30 %, red < 0) — server cost share, VAT and Stripe fees included.
export const ProfitDot = ({ c, testId }) => {
  const { t, locale } = useI18n();
  const fmtMoney = (v) => eur(v, locale);
  if (!c) return null;
  const share = c.share !== undefined ? ` · ${t("profit.share", { pct: Math.round(c.share * 1000) / 10 })}` : "";
  const label = t("profit.dotTitle", { pct: pctText(c), margin: fmtMoney(c.margin, "eur"), cost: fmtMoney(c.cost, "eur") }) + share;
  return <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${TONE[c.color]}`} title={label} aria-label={label} role="img" data-testid={testId} data-color={c.color} />;
};

// Server costs: two named monthly lines (store + mini-sites) and their total — editable here and in Admin → Paramètres.
export const ServerCosts = ({ p, onChange, testId = "profit" }) => {
  const { t, locale } = useI18n();
  const [vals, setVals] = useState({});
  const sig = (p?.server_lines || []).map((x) => x.eur).join("|");
  useEffect(() => {
    if (p) setVals(Object.fromEntries(p.server_lines.map((x) => [x.id, String(x.eur)])));
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return null;
  const num = (v) => Number(String(v || "").replace(",", ".")) || 0;
  const total = p.server_lines.reduce((s, x) => s + num(vals[x.id]), 0);
  const save = () => api.put("/admin/profit/bill", { lines: p.server_lines.map((x) => ({ id: x.id, eur: num(vals[x.id]) })) })
    .then(({ data }) => { onChange(data); toast.success(t("profit.saved")); }).catch((err) => toast.error(translateError(err, t)));
  return (
    <div className="min-w-0 space-y-2" data-testid={`${testId}-server-costs`}>
      {p.server_lines.map((x) => (
        <label key={x.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 text-xs text-neutral-300">
          <span className="min-w-0 break-words">{t(`profit.line.${x.id}`)}</span>
          <span className="flex items-center gap-1"><Input inputMode="decimal" value={vals[x.id] ?? ""} onChange={(e) => setVals({ ...vals, [x.id]: e.target.value })}
            className="h-9 w-28 bg-black/50 text-end" data-testid={`${testId}-line-${x.id}`} /><span className="text-neutral-500">{t("profit.perMonth")}</span></span>
        </label>
      ))}
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-2">
        <p className="break-words text-sm font-bold text-white" data-testid={`${testId}-bill-total`}>{t("profit.total", { amount: eur(total, locale) })}</p>
        <button type="button" className="btn-gold btn-sm" onClick={save} data-testid={`${testId}-bill-save`}>{t("profit.save")}</button>
      </div>
    </div>
  );
};

export const ServerCostsCard = () => {
  const { t } = useI18n();
  const [p, setP] = useProfit();
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-5" data-testid="settings-server-costs-card">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]"><Calculator className="h-4 w-4 shrink-0" /> {t("profit.costsTitle")}</h2>
      <ServerCosts p={p} onChange={setP} testId="settings-costs" />
    </section>
  );
};

export const ProfitCard = ({ p, onChange }) => {
  const { t, locale } = useI18n();
  const fmtMoney = (v) => eur(v, locale);
  if (!p) return null;
  const g = p.global;
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-5" data-testid="profit-card">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]"><Calculator className="h-4 w-4 shrink-0" /> {t("profit.title")}</h2>
      <ServerCosts p={p} onChange={onChange} />
      <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4">
        {[["net", g.net], ["cost", g.cost], ["margin", g.margin]].map(([k, v]) => (
          <div key={k} className="min-w-0 rounded-lg bg-black/30 p-2"><p className="break-words text-[0.65rem] uppercase tracking-wider text-neutral-500">{t(`profit.${k}`)}</p>
            <p className="font-bold text-white" data-testid={`profit-global-${k}`}>{fmtMoney(v, "eur")}</p></div>
        ))}
        <div className="min-w-0 rounded-lg bg-black/30 p-2"><p className="break-words text-[0.65rem] uppercase tracking-wider text-neutral-500">{t("profit.pct")}</p>
          <p className={`flex items-center gap-1.5 font-bold ${TEXT[g.color]}`} data-testid="profit-global-pct"><ProfitDot c={g} testId="profit-global-dot" /> {pctText(g)}</p></div>
      </div>
      <p className="break-words text-[0.7rem] text-neutral-500" data-testid="profit-formula">{t("profit.formula", { n: p.accounts })}</p>
    </section>
  );
};

// Offer form: live margin colour while negotiating (before the card is sent).
export const OfferProfit = ({ d, q, businesses }) => {
  const { t, locale } = useI18n();
  const fmtMoney = (v) => eur(v, locale);
  const [e, setE] = useState(null);
  const key = q ? `${q.monthly_ht}-${q.net_est}-${businesses}` : "";
  useEffect(() => {
    if (!q) return setE(null);
    const rate = q.fx_rate || 1; // USD per 1 EUR: monthly_ht is already EUR, net_est is in the offer currency
    const id = setTimeout(() => api.post("/admin/profit/estimate", { developer_id: d.id, monthly_ht: q.monthly_ht, businesses,
      net_monthly: q.net_est / Math.max(1, q.access_months) / rate }).then(({ data }) => setE(data)).catch(() => setE(null)), 300);
    return () => clearTimeout(id);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!e) return null;
  return (
    <p className={`flex min-w-0 flex-wrap items-center gap-1.5 break-words rounded-lg bg-black/40 px-2 py-1.5 text-xs font-semibold ${TEXT[e.color]}`} data-testid="offer-profit" data-color={e.color}>
      <ProfitDot c={e} testId="offer-profit-dot" /> {t("profit.offer", { pct: pctText(e), margin: fmtMoney(e.margin, "eur"), cost: fmtMoney(e.cost, "eur") })}
    </p>
  );
};
